import React, { useCallback, useContext, useEffect, useState } from 'react'
import { FilterType } from '@/components/Filter'
import { SelectFiltersFacade } from '@/components/Filter/ClientFields/SelectFiltersFacade'
import { errorToast, successToast, ToastContext } from '@/components/Toast/Toast'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { CompositeFilterDescriptor, SortDescriptor } from '@progress/kendo-data-query'
import { Grid, GridCellProps, GridColumn, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PlatformDownloadLinkDto } from 'api/Trading-Accounts/PlatformLink.Dto'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import MoreButton from 'components/MoreButton/MoreButton'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { isEmpty } from 'lodash'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import {
  fetchOperatingSystemsAction,
  fetchPlatformTypesAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { ResponseError, useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import {
  PlatformDownloadLinkForm,
  PlatformDownloadLinkFormValues,
} from './PlatformDownloadLinkForm'
import { platformLinkColumns } from './platformLinksColumns'
import { getPlatformLinksFields } from './platformLinksFields'

const usePlatformDownloadLinkFetch = () => {
  const dispatch = useAppDispatch()
  const identityApiClient = useApiClient(IdentityApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)
  const platformTypes = useAppSelector((state) => state.tradingAccounts.platformTypes)
  const operatingSystems = useAppSelector((state) => state.tradingAccounts.operatingSystems)

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [platformLinks, setPlatformLinks] = useState<PlatformDownloadLinkDto[]>([])

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => buildQuery(filter ?? gridState.filter) ?? null,
    [gridState.filter]
  )

  const getSortQuery = useCallback(
    (sortDescriptors?: SortDescriptor[]) => buildSortQuery(sortDescriptors ?? gridState.sort),
    [gridState.sort]
  )

  const fetchLinks = useCallback(
    async (args?: {
      search?: string | null
      sort?: string | null
      pageIndex?: number
      pageSize?: number
      skipCount?: boolean
    }) => {
      gridStatusActions.setLoading(true)
      try {
        const pageIndex = args?.pageIndex ?? gridState.pageIndex ?? 1
        const pageSize = args?.pageSize ?? gridState.pageSize ?? 10
        const search = args?.search !== undefined ? args.search : getQuery()
        const sort = args?.sort !== undefined ? args.sort : getSortQuery(gridState.sort)
        const skipCount = args?.skipCount ?? tableData?.skipCount !== false

        const response = await tradingAccountsApiClient.getPlatformDownloadLinks({
          search,
          sort,
          pageIndex,
          pageSize,
          skipCount,
        })

        setPlatformLinks(response?.items ?? [])
        setTableData((prev) => ({ ...prev, meta: response, skipCount }))

        gridActions.setGridFilters({
          pageIndex,
          pageSize,
          take: pageSize,
          skip: (pageIndex - 1) * pageSize,
          total: response?.totalResults ?? 0,
        })

        return response
      } finally {
        gridStatusActions.setLoading(false)
      }
    },
    [
      gridActions,
      gridState.pageIndex,
      gridState.pageSize,
      gridState.sort,
      gridStatusActions,
      getQuery,
      getSortQuery,
      tableData?.skipCount,
      tradingAccountsApiClient,
    ]
  )

  useEffect(() => {
    const initial = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    gridActions.setGridFilters(initial)

    void fetchLinks({
      search: getQuery(initial.filter),
      sort: getSortQuery(initial.sort),
      pageIndex: initial.pageIndex ?? 1,
      pageSize: initial.pageSize ?? gridState.pageSize ?? 10,
      skipCount: tableData?.skipCount !== false,
    })

    if (isEmpty(tickmillCompanies)) dispatch(fetchTickmillCompaniesAction(identityApiClient))
    if (isEmpty(platformTypes)) dispatch(fetchPlatformTypesAction(tradingAccountsApiClient))
    if (isEmpty(operatingSystems)) dispatch(fetchOperatingSystemsAction(tradingAccountsApiClient))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const search = params?.query !== undefined ? params.query : getQuery()
      await fetchLinks({
        search,
        pageIndex: 1,
      })
    },
    [fetchLinks, getQuery]
  )

  const handleSkipCountChange = useCallback(
    async (skipCount: boolean) => {
      await fetchLinks({ skipCount })
    },
    [fetchLinks]
  )

  const handleGridPageChange = useCallback(
    async (page: number) => {
      await fetchLinks({ pageIndex: page })
    },
    [fetchLinks]
  )

  const handlePageSizeChange = useCallback(
    async (pageSize: number) => {
      await fetchLinks({ pageIndex: 1, pageSize })
    },
    [fetchLinks]
  )

  const handleSortChange = useCallback(
    async (event: GridSortChangeEvent) => {
      gridActions.setGridFilters({ sort: event.sort })
      await fetchLinks({
        sort: getSortQuery(event.sort),
      })
    },
    [fetchLinks, getSortQuery, gridActions]
  )

  return {
    gridState,
    gridActions,
    gridStatusState,
    gridStatusActions,

    tableData,
    setTableData,

    platformLinks,
    setPlatformLinks,

    tickmillCompanies,
    platformTypes,
    operatingSystems,

    getQuery,
    getSortQuery,
    fetchLinks,

    filterFetch,
    handleSkipCountChange,
    handleGridPageChange,
    handlePageSizeChange,
    handleSortChange,
  }
}

type ModalProps = {
  data?: PlatformDownloadLinkDto
  visible: boolean
}

const initialModalState: ModalProps = { data: undefined, visible: false }

const useModals = () => {
  const [platformDownloadLinkModal, setPlatformDownloadLinkModal] =
    useState<ModalProps>(initialModalState)

  const [auditLogModal, setAuditLogModal] = useState<ModalProps>(initialModalState)

  const handleAddPlatformLinkModalOpen = () =>
    setPlatformDownloadLinkModal({ data: undefined, visible: true })

  const handleUpdatePlatformLinkModalOpen = (data?: PlatformDownloadLinkDto) =>
    setPlatformDownloadLinkModal({ data, visible: true })

  const handlePlatformLinkModalClose = () =>
    setPlatformDownloadLinkModal({ data: undefined, visible: false })

  const setAuditLog = (record: PlatformDownloadLinkDto) =>
    setAuditLogModal({ data: record, visible: true })

  return {
    auditLogModal,
    setAuditLog,
    setAuditLogModal,

    platformDownloadLinkModal,
    handleAddPlatformLinkModalOpen,
    handleUpdatePlatformLinkModalOpen,
    handlePlatformLinkModalClose,
  }
}

const useMutate = (
  modals: ReturnType<typeof useModals>,
  platformLinkFetch: ReturnType<typeof usePlatformDownloadLinkFetch>
) => {
  const { handlePlatformLinkModalClose, platformDownloadLinkModal } = modals
  const setToast = useContext(ToastContext)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const handleSubmit = async (values: PlatformDownloadLinkFormValues) => {
    try {
      const { data } = platformDownloadLinkModal
      handlePlatformLinkModalClose()

      if (data) {
        await tradingAccountsApiClient.updatePlatformDownloadLink(data.id, values)
        setToast(successToast('Platform Download Link updated successfully.'))
      } else {
        await tradingAccountsApiClient.createPlatformDownloadLink(values)
        setToast(successToast('Platform Download Link added successfully.'))
      }
    } catch (error) {
      if (error instanceof ResponseError && error?.response?.message) {
        setToast(errorToast(error.response.message))
      }
      console.error('Failed to submit platform download link:', error)
    } finally {
      await platformLinkFetch.fetchLinks()
    }
  }

  return { handleSubmit }
}

export function PlatformDownloadLinksPage() {
  const crumbs = [{ title: 'Platform Links', link: links.administrationPlatformLinks.url }]

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const platformLinkFetch = usePlatformDownloadLinkFetch()
  const modals = useModals()
  const mutate = useMutate(modals, platformLinkFetch)

  const { gridState, gridActions } = platformLinkFetch
  const { filterFetch } = platformLinkFetch
  const { handleAddPlatformLinkModalOpen } = modals

  return (
    <>
      <Modals {...modals} {...mutate} {...platformLinkFetch} />

      <Breadcrumbs crumbs={crumbs} />

      <SelectFiltersFacade
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.PlatformDownloadLinks}
        gridState={gridState}
        filterFields={getPlatformLinksFields()}
      />

      {hasPermission(permissions, PermissionsEnum.CanCreatePlatformLinks) && (
        <AddButton text='Add Platform Download Link' onClick={handleAddPlatformLinkModalOpen} />
      )}

      <Table {...platformLinkFetch} {...modals} />
    </>
  )
}

type TableProps = ReturnType<typeof usePlatformDownloadLinkFetch> & ReturnType<typeof useModals>

const Table: React.FC<TableProps> = (props) => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const {
    gridState,
    tableData,
    setTableData,
    platformLinks,
    handlePageSizeChange,
    handleGridPageChange,
    handleSkipCountChange,
    handleSortChange,
    handleUpdatePlatformLinkModalOpen,
    setAuditLog,
  } = props

  return (
    <TableStateContext.Provider value={[tableData, setTableData]}>
      <Grid
        {...gridState}
        resizable
        data={platformLinks}
        sort={gridState.sort}
        onSortChange={handleSortChange}
        total={gridState.total}
        take={gridState.take}
        skip={gridState.skip}
        pageable
        pager={() => (
          <Pager
            onSkipCountChange={handleSkipCountChange}
            onPageChange={handleGridPageChange}
            onPageSizeChange={handlePageSizeChange}
            page={gridState.pageIndex}
            pageSize={gridState.pageSize}
            take={gridState.take || 10}
            skip={gridState.skip || 0}
          />
        )}
        style={{ zIndex: 0 }}
      >
        {platformLinkColumns.map((column, idx) => (
          <GridColumn key={idx + 2} {...column} />
        ))}

        <GridColumn
          width={50}
          title=''
          cell={(gridCellProps: GridCellProps) => (
            <td>
              {hasPermission(permissions, PermissionsEnum.CanEditPlatformLinks) && (
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      onClick: () => handleUpdatePlatformLinkModalOpen(gridCellProps.dataItem),
                    },
                    {
                      name: 'Audit Log',
                      disabled: !hasPermission(
                        permissions,
                        PermissionsEnum.CanViewPlatformLinksAuditLog
                      ),
                      'data-test': 'audit-log',
                      onClick: () => setAuditLog(gridCellProps.dataItem),
                    },
                  ]}
                />
              )}
            </td>
          )}
        />
      </Grid>
    </TableStateContext.Provider>
  )
}

type ModalsType = ReturnType<typeof useModals> &
  ReturnType<typeof usePlatformDownloadLinkFetch> &
  ReturnType<typeof useMutate>

const Modals: React.FC<ModalsType> = (props) => {
  const { platformDownloadLinkModal, handlePlatformLinkModalClose } = props
  const { platformTypes, operatingSystems, tickmillCompanies, handleSubmit } = props
  const { auditLogModal, setAuditLogModal } = props

  return (
    <>
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal.visible}
          onClose={() => setAuditLogModal({ visible: false, data: undefined })}
          platformDownloadLinkId={auditLogModal.data.id}
        />
      )}

      {platformDownloadLinkModal.visible && (
        <PlatformDownloadLinkForm
          isOpen={platformDownloadLinkModal.visible}
          onClose={handlePlatformLinkModalClose}
          data={platformDownloadLinkModal.data}
          tickmillCompanies={tickmillCompanies}
          platformTypes={platformTypes}
          operatingSystems={operatingSystems}
          onSubmit={handleSubmit}
        />
      )}
    </>
  )
}
